import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  Building2, Users, FileText, CheckCircle, ArrowRight, 
  Calendar, BookOpen, Clock, AlertTriangle, ChevronRight, Vote
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const AssembleeGenerale = () => {
  const tableOfContents = [
    { id: "definition", title: "Qu'est-ce qu'une AG de copropriété ?" },
    { id: "types", title: "Types d'assemblées générales" },
    { id: "convocation", title: "La convocation à l'AG" },
    { id: "ordre-jour", title: "L'ordre du jour" },
    { id: "quorum", title: "Quorum et majorités" },
    { id: "deroulement", title: "Déroulement de l'AG" },
    { id: "pv", title: "Le procès-verbal" },
    { id: "contestation", title: "Contestation des décisions" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Assemblée Générale de Copropriété : Guide Complet 2025 | Organisation, convocation, vote</title>
        <meta name="description" content="Guide complet sur l'assemblée générale de copropriété. Convocation, ordre du jour, quorum, majorités de vote, procès-verbal. Tout savoir pour organiser votre AG." />
        <meta name="keywords" content="assemblée générale copropriété, AG copropriété, convocation AG, ordre du jour AG, procès-verbal AG, majorité copropriété, vote copropriété, quorum AG" />
        <link rel="canonical" href="https://copropriete.app/assemblee-generale-copropriete" />
      </Helmet>

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">copropriete.app</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => window.location.href = '/login'}>
                Se connecter
              </Button>
              <Button onClick={() => window.location.href = '/login'} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                C'est gratuit
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Guide complet 2025
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-6">
              Assemblée Générale de Copropriété
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Tout ce que vous devez savoir pour organiser et participer efficacement à une assemblée générale de copropriété.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-24 bg-slate-50 rounded-2xl p-6">
              <h3 className="font-bold text-slate-900 mb-4">Sommaire</h3>
              <nav className="space-y-2">
                {tableOfContents.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors text-sm py-1"
                  >
                    <ChevronRight className="w-4 h-4" />
                    {item.title}
                  </a>
                ))}
              </nav>
              
              <div className="mt-8 p-4 bg-blue-100 rounded-xl">
                <p className="text-sm text-blue-800 font-medium mb-3">
                  Organisez vos AG facilement avec copropriete.app
                </p>
                <Button size="sm" onClick={() => window.location.href = '/login'} className="w-full bg-blue-600 hover:bg-blue-700">
                  C'est gratuit 7 jours
                </Button>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 prose prose-slate prose-lg max-w-none">
            <section id="definition" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-600" />
                Qu'est-ce qu'une AG de copropriété ?
              </h2>
              <p>
                L'<strong>assemblée générale de copropriété</strong> (AG) est la réunion annuelle obligatoire de tous les copropriétaires d'un immeuble. C'est l'organe de décision souverain de la copropriété qui permet de :
              </p>
              <ul>
                <li>Voter les décisions importantes concernant l'immeuble</li>
                <li>Approuver les comptes de l'exercice écoulé</li>
                <li>Voter le budget prévisionnel</li>
                <li>Élire ou renouveler le conseil syndical</li>
                <li>Décider des travaux à réaliser</li>
              </ul>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-xl my-8">
                <p className="text-blue-900 font-medium m-0">
                  💡 <strong>Bon à savoir :</strong> L'assemblée générale doit se tenir au moins une fois par an dans les 6 mois suivant la clôture de l'exercice comptable.
                </p>
              </div>
            </section>

            <section id="types" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Calendar className="w-8 h-8 text-blue-600" />
                Types d'assemblées générales
              </h2>
              
              <h3 className="text-xl font-bold text-slate-800">AG ordinaire annuelle</h3>
              <p>
                C'est l'assemblée obligatoire qui se tient chaque année. Elle traite les questions courantes : approbation des comptes, budget, travaux d'entretien courant, élection du conseil syndical.
              </p>

              <h3 className="text-xl font-bold text-slate-800">AG extraordinaire</h3>
              <p>
                Convoquée en cas d'urgence ou pour traiter de questions exceptionnelles ne pouvant attendre l'AG annuelle : travaux urgents, modification du règlement de copropriété, changement de syndic.
              </p>

              <h3 className="text-xl font-bold text-slate-800">AG sur seconde convocation</h3>
              <p>
                Si le quorum n'est pas atteint lors de la première AG, une seconde assemblée peut être convoquée sans condition de quorum.
              </p>
            </section>

            <section id="convocation" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-600" />
                La convocation à l'AG
              </h2>

              <h3 className="text-xl font-bold text-slate-800">Qui convoque ?</h3>
              <p>
                Le syndic est responsable de la convocation de l'assemblée générale. En cas de carence du syndic, le président du conseil syndical ou tout copropriétaire peut convoquer l'AG.
              </p>

              <h3 className="text-xl font-bold text-slate-800">Délai de convocation</h3>
              <p>
                La convocation doit être envoyée <strong>au moins 21 jours</strong> avant la date de l'assemblée (sauf urgence où ce délai peut être réduit à 15 jours ou moins).
              </p>

              <h3 className="text-xl font-bold text-slate-800">Contenu obligatoire de la convocation</h3>
              <ul>
                <li>Date, heure et lieu de l'assemblée</li>
                <li>Ordre du jour détaillé</li>
                <li>Texte des résolutions proposées</li>
                <li>Documents annexes (comptes, budget, devis...)</li>
                <li>Formulaire de pouvoir</li>
              </ul>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-xl my-8">
                <p className="text-yellow-900 font-medium m-0">
                  ⚠️ <strong>Attention :</strong> Une convocation non conforme peut entraîner la nullité des décisions prises en assemblée générale.
                </p>
              </div>
            </section>

            <section id="ordre-jour" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                L'ordre du jour
              </h2>
              <p>
                L'ordre du jour liste toutes les questions qui seront débattues et votées lors de l'AG. Il doit être précis et complet car <strong>seules les questions inscrites à l'ordre du jour peuvent faire l'objet d'un vote</strong>.
              </p>

              <h3 className="text-xl font-bold text-slate-800">Points obligatoires</h3>
              <ul>
                <li>Approbation des comptes de l'exercice clos</li>
                <li>Vote du budget prévisionnel</li>
                <li>Approbation de la gestion du syndic (quitus)</li>
              </ul>

              <h3 className="text-xl font-bold text-slate-800">Demander l'inscription d'une question</h3>
              <p>
                Tout copropriétaire peut demander l'inscription d'une question à l'ordre du jour en adressant sa demande au syndic par lettre recommandée, avant l'envoi des convocations.
              </p>
            </section>

            <section id="quorum" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Vote className="w-8 h-8 text-blue-600" />
                Quorum et majorités de vote
              </h2>

              <h3 className="text-xl font-bold text-slate-800">Le quorum</h3>
              <p>
                Il n'y a <strong>pas de quorum obligatoire</strong> pour l'assemblée générale de copropriété. Cependant, si moins d'1/4 des voix sont représentées, une nouvelle AG peut être convoquée.
              </p>

              <h3 className="text-xl font-bold text-slate-800">Les différentes majorités</h3>
              
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="px-4 py-3 text-left">Majorité</th>
                      <th className="px-4 py-3 text-left">Règle</th>
                      <th className="px-4 py-3 text-left">Exemples de décisions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="px-4 py-3 font-medium">Article 24</td>
                      <td className="px-4 py-3">Majorité des présents et représentés</td>
                      <td className="px-4 py-3">Approbation des comptes, travaux d'entretien courant</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 font-medium">Article 25</td>
                      <td className="px-4 py-3">Majorité de tous les copropriétaires</td>
                      <td className="px-4 py-3">Désignation du syndic, travaux d'amélioration</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 font-medium">Article 26</td>
                      <td className="px-4 py-3">Double majorité (2/3 des voix de tous)</td>
                      <td className="px-4 py-3">Modification du règlement, actes de disposition</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Unanimité</td>
                      <td className="px-4 py-3">Tous les copropriétaires</td>
                      <td className="px-4 py-3">Suppression d'un équipement commun, changement de destination</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section id="deroulement" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Clock className="w-8 h-8 text-blue-600" />
                Déroulement de l'AG
              </h2>

              <ol className="space-y-4">
                <li><strong>1. Émargement et vérification des pouvoirs</strong> - Chaque copropriétaire signe la feuille de présence</li>
                <li><strong>2. Désignation du président de séance</strong> - Un copropriétaire (non syndic) est élu pour présider</li>
                <li><strong>3. Désignation des scrutateurs</strong> - Facultatif, ils aident au dépouillement des votes</li>
                <li><strong>4. Désignation du secrétaire</strong> - Souvent le syndic, il rédige le procès-verbal</li>
                <li><strong>5. Examen et vote des résolutions</strong> - Chaque point est débattu puis soumis au vote</li>
                <li><strong>6. Questions diverses</strong> - Discussions libres sans vote possible</li>
              </ol>
            </section>

            <section id="pv" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-600" />
                Le procès-verbal
              </h2>
              <p>
                Le <strong>procès-verbal</strong> (PV) est le document officiel qui retranscrit les décisions prises en AG. Il doit être :
              </p>
              <ul>
                <li>Rédigé par le secrétaire de séance</li>
                <li>Signé par le président, le secrétaire et les scrutateurs</li>
                <li>Envoyé à tous les copropriétaires dans les <strong>2 mois</strong> suivant l'AG</li>
                <li>Notifié aux copropriétaires absents et opposants</li>
              </ul>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl my-8">
                <p className="text-emerald-900 font-medium m-0">
                  ✅ <strong>Avec copropriete.app</strong>, générez automatiquement vos PV d'assemblée générale conformes à la législation.
                </p>
              </div>
            </section>

            <section id="contestation" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-blue-600" />
                Contestation des décisions
              </h2>
              <p>
                Les décisions d'AG peuvent être contestées devant le tribunal judiciaire dans un délai de <strong>2 mois</strong> suivant la notification du PV.
              </p>

              <h3 className="text-xl font-bold text-slate-800">Motifs de contestation</h3>
              <ul>
                <li>Vice de forme (convocation irrégulière, délai non respecté)</li>
                <li>Vice de fond (abus de majorité, décision contraire à la loi)</li>
                <li>Non-inscription de la question à l'ordre du jour</li>
                <li>Erreur dans le calcul des voix</li>
              </ul>

              <h3 className="text-xl font-bold text-slate-800">Qui peut contester ?</h3>
              <p>
                Seuls les copropriétaires <strong>opposants</strong> (ayant voté contre) ou <strong>défaillants</strong> (absents non représentés) peuvent contester une décision.
              </p>
            </section>

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 my-8 border border-blue-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Simplifiez vos assemblées générales</h3>
              <p className="text-slate-700 mb-6">
                copropriete.app vous accompagne dans toute l'organisation de vos AG : convocations, feuilles de présence, votes, PV automatiques.
              </p>
              <Button onClick={() => window.location.href = '/login'} size="lg" className="bg-blue-600 hover:bg-blue-700">
                Essayer gratuitement pendant 7 jours
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">copropriete.app</span>
          </Link>
          <p className="text-sm">© 2025 copropriete.app. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default AssembleeGenerale;
